import React, { useState, useRef, useEffect } from 'react';
import { API_ENDPOINTS } from '../../config/api';
import {
  Box,
  Button,
  Typography,
  Alert,
  LinearProgress,
  Chip,
  IconButton,
} from '@mui/material';
import {
  Mic,
  Stop,
  PlayArrow,
  Pause,
  Delete,
  CheckCircle,
  Refresh,
  CloudOff,
} from '@mui/icons-material';

interface AudioRecorderProps {
  onAudioReady: (audioUrl: string, duration: number) => void;
  onError: (error: string) => void;
  websocketUrl: string;
  assessmentId: string;
  questionId: string;
  assignmentQuestionId?: string;
  maxDuration?: number;
  disabled?: boolean;
  autoStart?: boolean;
}

interface RecordingState {
  isRecording: false | 'starting' | 'recording' | 'stopping';
  isPlaying: boolean;
  duration: number;
  audioUrl: string | null;
  error: string | null;
  uploadProgress: number;
  isUploading: boolean;
  retryCount: number;
  canRetry: boolean;
  isOnline: boolean;
}

const AudioRecorder: React.FC<AudioRecorderProps> = ({
  onAudioReady,
  onError,
  websocketUrl,
  assessmentId,
  questionId,
  assignmentQuestionId,
  maxDuration = 120,
  disabled = false,
  autoStart = false,
}) => {
  // State management
  const [state, setState] = useState<RecordingState>({
    isRecording: false,
    isPlaying: false,
    duration: 0,
    audioUrl: null,
    error: null,
    uploadProgress: 0,
    isUploading: false,
    retryCount: 0,
    canRetry: false,
    isOnline: navigator.onLine,
  });

  // Refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const websocketRef = useRef<WebSocket | null>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pendingAudioBlobRef = useRef<Blob | null>(null);
  const uploadAbortControllerRef = useRef<AbortController | null>(null);
  const isComponentMountedRef = useRef<boolean>(true);

  // Helper function to check if error is transient (can retry) or permanent
  const isTransientError = (error: any, statusCode?: number): boolean => {
    if (statusCode) {
      // Permanent errors (don't retry)
      if ([400, 401, 403, 413, 415, 422].includes(statusCode)) return false;
      // Transient errors (can retry)
      if ([408, 429, 500, 502, 503, 504].includes(statusCode)) return true;
    }

    if (error instanceof Error) {
      const message = error.message.toLowerCase();
      // Network-related errors are usually transient
      if (message.includes('network') ||
          message.includes('failed to fetch') ||
          message.includes('connection') ||
          message.includes('timeout') ||
          error.name === 'NetworkError' ||
          error.name === 'AbortError') {
        return true;
      }
    }

    // Default to not retrying unknown errors
    return false;
  };

  // Upload audio with XMLHttpRequest for progress tracking and retry logic
  const uploadAudioToAPI = async (audioBlob: Blob, retryAttempt: number = 0): Promise<void> => {
    const MAX_RETRIES = 3;
    const UPLOAD_TIMEOUT = 15 * 60 * 1000; // 15 minutes
    const fileSizeMB = audioBlob.size / 1024 / 1024;

    // Store blob for potential retry
    pendingAudioBlobRef.current = audioBlob;

    console.log(`🎵 Upload attempt ${retryAttempt + 1}/${MAX_RETRIES + 1}...`);
    console.log('   Blob size:', audioBlob.size, 'bytes', `(${fileSizeMB.toFixed(2)}MB)`);
    console.log('   Blob type:', audioBlob.type);
    console.log('   API endpoint:', API_ENDPOINTS.AUDIO_UPLOAD);

    // Determine filename based on blob type
    let filename = 'recording.mp3';
    if (audioBlob.type.includes('webm')) filename = 'recording.webm';
    else if (audioBlob.type.includes('mp4')) filename = 'recording.mp4';
    else if (audioBlob.type.includes('wav')) filename = 'recording.wav';
    else if (audioBlob.type.includes('ogg')) filename = 'recording.ogg';

    // Create FormData
    const formData = new FormData();
    formData.append('audio_file', audioBlob, filename);
    formData.append('assessment_id', assessmentId);
    formData.append('question_id', questionId);
    formData.append('question_type', 'speaking');
    if (assignmentQuestionId) {
      formData.append('assignment_question_id', assignmentQuestionId);
    }

    return new Promise<void>((resolve, reject) => {
      // Create XMLHttpRequest for progress tracking
      const xhr = new XMLHttpRequest();
      uploadAbortControllerRef.current = new AbortController();

      // Timeout handling
      const timeoutId = setTimeout(() => {
        xhr.abort();
        reject(new Error('Upload timeout after 15 minutes'));
      }, UPLOAD_TIMEOUT);

      // Upload progress tracking (real progress!)
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && isComponentMountedRef.current) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          setState(prev => ({ ...prev, uploadProgress: percentComplete }));
          console.log(`   Upload progress: ${percentComplete}% (${event.loaded}/${event.total} bytes)`);
        }
      });

      // Upload complete (doesn't mean success, just finished sending)
      xhr.upload.addEventListener('load', () => {
        console.log('   Upload finished, waiting for server response...');
      });

      // Network error during upload
      xhr.upload.addEventListener('error', () => {
        clearTimeout(timeoutId);
        console.error('   Network error during upload');
      });

      // Response received
      xhr.addEventListener('load', async () => {
        clearTimeout(timeoutId);

        if (!isComponentMountedRef.current) {
          reject(new Error('Component unmounted'));
          return;
        }

        try {
          const statusCode = xhr.status;
          console.log('   Response status:', statusCode);

          if (statusCode >= 200 && statusCode < 300) {
            // Success
            const result = JSON.parse(xhr.responseText);
            console.log('   Upload successful:', result);

            setState(prev => ({
              ...prev,
              uploadProgress: 100,
              isUploading: false,
              retryCount: 0,
              canRetry: false,
              error: null,
            }));

            // Validate response
            if (!result.data || !result.data.audio_url) {
              reject(new Error('Invalid response: missing audio_url'));
              return;
            }

            const audioUrl = result.data.audio_url;
            const duration = result.data.duration_seconds || 0;

            console.log('   Final audio URL:', audioUrl);
            console.log('   Duration:', duration, 'seconds');
            console.log('✅ Upload completed successfully!');

            pendingAudioBlobRef.current = null;
            onAudioReady(audioUrl, duration);
            resolve();

          } else {
            // Error response
            let errorData: any = { detail: 'Unknown error' };
            try {
              errorData = JSON.parse(xhr.responseText);
            } catch (e) {
              // Response not JSON
            }

            console.error('   Upload failed with status:', statusCode, errorData);

            // Build error message
            let errorMessage = errorData.detail || `Upload failed with status ${statusCode}`;
            if (statusCode === 413) {
              errorMessage = errorData.detail || `File too large (${fileSizeMB.toFixed(1)}MB). Please try a shorter recording.`;
            } else if (statusCode === 500) {
              errorMessage = 'Server error processing audio. Please try again or contact support.';
            } else if (statusCode === 503) {
              errorMessage = 'Service temporarily unavailable. Please try again in a moment.';
            } else if (statusCode === 504) {
              errorMessage = 'Upload timeout on server. File may be too large or server is busy.';
            }

            const error = new Error(errorMessage);
            const canRetry = isTransientError(error, statusCode);

            // Retry logic for transient errors
            if (canRetry && retryAttempt < MAX_RETRIES && isComponentMountedRef.current) {
              const backoffTime = Math.min(1000 * Math.pow(2, retryAttempt), 10000);
              console.log(`   Retrying in ${backoffTime}ms... (attempt ${retryAttempt + 1}/${MAX_RETRIES})`);

              setState(prev => ({
                ...prev,
                uploadProgress: 0,
                retryCount: retryAttempt + 1,
                error: `${errorMessage} Retrying...`,
              }));

              setTimeout(() => {
                uploadAudioToAPI(audioBlob, retryAttempt + 1)
                  .then(resolve)
                  .catch(reject);
              }, backoffTime);
            } else {
              // No more retries or permanent error
              setState(prev => ({
                ...prev,
                uploadProgress: 0,
                isUploading: false,
                canRetry: canRetry,
                error: errorMessage,
              }));
              reject(error);
            }
          }
        } catch (error) {
          clearTimeout(timeoutId);
          console.error('   Error processing response:', error);
          setState(prev => ({
            ...prev,
            uploadProgress: 0,
            isUploading: false,
            canRetry: true,
            error: 'Failed to process server response',
          }));
          reject(error);
        }
      });

      // Network error
      xhr.addEventListener('error', () => {
        clearTimeout(timeoutId);
        console.error('❌ Network error during upload');

        const error = new Error('Network error. Please check your internet connection and try again.');
        const canRetry = isTransientError(error);

        // Auto-retry on network errors
        if (canRetry && retryAttempt < MAX_RETRIES && isComponentMountedRef.current) {
          const backoffTime = Math.min(1000 * Math.pow(2, retryAttempt), 10000);
          console.log(`   Retrying in ${backoffTime}ms... (attempt ${retryAttempt + 1}/${MAX_RETRIES})`);

          setState(prev => ({
            ...prev,
            uploadProgress: 0,
            retryCount: retryAttempt + 1,
            error: 'Network error. Retrying...',
          }));

          setTimeout(() => {
            uploadAudioToAPI(audioBlob, retryAttempt + 1)
              .then(resolve)
              .catch(reject);
          }, backoffTime);
        } else {
          setState(prev => ({
            ...prev,
            uploadProgress: 0,
            isUploading: false,
            canRetry: true,
            error: error.message,
          }));
          reject(error);
        }
      });

      // Abort
      xhr.addEventListener('abort', () => {
        clearTimeout(timeoutId);
        console.log('   Upload aborted');

        const error = new Error('Upload cancelled');
        setState(prev => ({
          ...prev,
          uploadProgress: 0,
          isUploading: false,
          canRetry: true,
          error: error.message,
        }));
        reject(error);
      });

      // Timeout
      xhr.addEventListener('timeout', () => {
        clearTimeout(timeoutId);
        console.error('   Upload timeout');

        const error = new Error('Upload timeout. Please try again with a better connection.');
        const canRetry = isTransientError(error);

        if (canRetry && retryAttempt < MAX_RETRIES && isComponentMountedRef.current) {
          const backoffTime = Math.min(1000 * Math.pow(2, retryAttempt), 10000);
          console.log(`   Retrying in ${backoffTime}ms... (attempt ${retryAttempt + 1}/${MAX_RETRIES})`);

          setState(prev => ({
            ...prev,
            uploadProgress: 0,
            retryCount: retryAttempt + 1,
            error: 'Upload timeout. Retrying...',
          }));

          setTimeout(() => {
            uploadAudioToAPI(audioBlob, retryAttempt + 1)
              .then(resolve)
              .catch(reject);
          }, backoffTime);
        } else {
          setState(prev => ({
            ...prev,
            uploadProgress: 0,
            isUploading: false,
            canRetry: true,
            error: error.message,
          }));
          reject(error);
        }
      });

      // Start upload
      setState(prev => ({
        ...prev,
        isUploading: true,
        uploadProgress: 0,
        retryCount: retryAttempt,
      }));

      xhr.open('POST', API_ENDPOINTS.AUDIO_UPLOAD);
      xhr.send(formData);
    });
  };


  // Start recording
  const startRecording = async () => {
    if (disabled || state.isRecording) return;

    try {
      setState(prev => ({ ...prev, isRecording: 'starting', error: null }));

      // Check microphone permissions first
      const permissionStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });
      if (permissionStatus.state === 'denied') {
        throw new Error('Microphone permission denied. Please allow microphone access and try again.');
      }

      // Get user media with fallback options
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            sampleRate: 44100,
          },
        });
      } catch (err) {
        // Fallback to basic audio constraints
        stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
      }

      streamRef.current = stream;
      audioChunksRef.current = [];

      // Create MediaRecorder with MP3-compatible mime types
      let mediaRecorder: MediaRecorder;
      const mimeTypes = [
        'audio/mpeg',           // MP3 (preferred)
        'audio/mp4',            // MP4 audio (compatible)
        'audio/webm;codecs=opus', // WebM fallback
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/wav'
      ];
      
      let selectedMimeType = '';
      for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          selectedMimeType = mimeType;
          break;
        }
      }
      
      if (!selectedMimeType) {
        throw new Error('No supported audio format found for recording.');
      }
      
      mediaRecorder = new MediaRecorder(stream, {
        mimeType: selectedMimeType,
      });
      
      console.log('Using audio format:', selectedMimeType);

      mediaRecorderRef.current = mediaRecorder;

      // Handle data available
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
          console.log('Audio chunk received:', event.data.size, 'bytes');
          
          // For now, skip WebSocket streaming and just collect chunks locally
          // This ensures recording works even without WebSocket connection
        }
      };

      // Handle recording stop
      mediaRecorder.onstop = async () => {
        console.log('Recording stopped, processing audio...');
        
        // Create local audio blob for preview
        const audioBlob = new Blob(audioChunksRef.current, { type: selectedMimeType });
        const audioUrl = URL.createObjectURL(audioBlob);
        
        console.log('Audio blob created:', audioBlob.size, 'bytes');
        
        setState(prev => ({
          ...prev,
          isRecording: false,
          audioUrl,
          uploadProgress: 10, // Start upload progress
        }));
        
        // Upload to backend API
        try {
          await uploadAudioToAPI(audioBlob);
        } catch (error) {
          console.error('Failed to upload audio:', error);
          onError('Failed to upload audio recording');
        }
      };

      // Start recording
      mediaRecorder.start(1000); // Collect data every second
      console.log('Recording started successfully');

      setState(prev => ({ ...prev, isRecording: 'recording', duration: 0 }));

      // Start duration timer
      recordingTimerRef.current = setInterval(() => {
        setState(prev => {
          const newDuration = prev.duration + 1;
          
          // Auto-stop at max duration
          if (newDuration >= maxDuration) {
            stopRecording();
            return { ...prev, duration: maxDuration };
          }
          
          return { ...prev, duration: newDuration };
        });
      }, 1000);

    } catch (err) {
      console.error('Error starting recording:', err);
      let errorMessage = 'Failed to start recording.';
      
      if (err instanceof Error) {
        if (err.message.includes('Permission denied') || err.name === 'NotAllowedError') {
          errorMessage = 'Microphone permission denied. Please allow microphone access and try again.';
        } else if (err.name === 'NotFoundError') {
          errorMessage = 'No microphone found. Please connect a microphone and try again.';
        } else {
          errorMessage = err.message;
        }
      }
      
      setState(prev => ({
        ...prev,
        isRecording: false,
        error: errorMessage,
      }));
      onError(errorMessage);
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (!state.isRecording || state.isRecording === 'starting' || state.isRecording === 'stopping') return;

    setState(prev => ({ ...prev, isRecording: 'stopping' }));

    // Clear timer
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }

    // Stop media recorder
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }

    // Stop media stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  // Play/pause audio
  const togglePlayback = () => {
    if (!state.audioUrl) return;

    if (!audioElementRef.current) {
      audioElementRef.current = new Audio(state.audioUrl);
      audioElementRef.current.onended = () => {
        setState(prev => ({ ...prev, isPlaying: false }));
      };
    }

    if (state.isPlaying) {
      audioElementRef.current.pause();
      setState(prev => ({ ...prev, isPlaying: false }));
    } else {
      audioElementRef.current.play();
      setState(prev => ({ ...prev, isPlaying: true }));
    }
  };

  // Delete recording
  const deleteRecording = () => {
    if (audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current = null;
    }

    if (state.audioUrl) {
      URL.revokeObjectURL(state.audioUrl);
    }

    setState(prev => ({
      ...prev,
      audioUrl: null,
      isPlaying: false,
      duration: 0,
      uploadProgress: 0,
      error: null,
      canRetry: false,
    }));

    // Clear pending blob
    pendingAudioBlobRef.current = null;
  };

  // Manual retry function
  const retryUpload = async () => {
    if (!pendingAudioBlobRef.current) {
      console.error('No pending audio to retry');
      return;
    }

    setState(prev => ({ ...prev, error: null, canRetry: false }));

    try {
      await uploadAudioToAPI(pendingAudioBlobRef.current, 0);
    } catch (error) {
      console.error('Retry failed:', error);
      onError('Failed to upload audio recording');
    }
  };

  // Reset component when question changes
  useEffect(() => {
    // Clean up previous recording state when question changes
    console.log('Question changed, resetting AudioRecorder');

    // Stop any ongoing recording
    if (state.isRecording !== false) {
      // Clear recording timer
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }

      // Stop media recorder
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }

      // Stop media stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    }

    // Clear audio playback
    if (audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current = null;
    }

    // Revoke old audio URL
    if (state.audioUrl) {
      URL.revokeObjectURL(state.audioUrl);
    }

    // Reset state for new question
    setState({
      isRecording: false,
      isPlaying: false,
      duration: 0,
      audioUrl: null,
      error: null,
      uploadProgress: 0,
      isUploading: false,
      retryCount: 0,
      canRetry: false,
      isOnline: navigator.onLine,
    });

    // Clear pending blob
    pendingAudioBlobRef.current = null;
    audioChunksRef.current = [];

  }, [questionId, assignmentQuestionId]); // Reset when question changes

  // Auto-start recording if enabled
  useEffect(() => {
    if (autoStart && !disabled) {
      startRecording();
    }
  }, [autoStart, disabled]);

  // Network state monitoring
  useEffect(() => {
    const handleOnline = () => {
      console.log('🌐 Network connected');
      setState(prev => ({ ...prev, isOnline: true }));

      // Auto-retry if there's a pending upload and we're online
      if (pendingAudioBlobRef.current && state.canRetry && !state.isUploading) {
        console.log('🔄 Auto-retrying upload after network reconnection');
        retryUpload();
      }
    };

    const handleOffline = () => {
      console.log('📡 Network disconnected');
      setState(prev => ({
        ...prev,
        isOnline: false,
        error: state.isUploading
          ? 'Network disconnected. Upload paused. Will retry when connection is restored.'
          : prev.error,
      }));
    };

    // Handle network type changes (WiFi <-> cellular)
    const handleConnectionChange = () => {
      const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
      if (connection) {
        console.log('🔄 Network type changed:', connection.effectiveType);
        // If we're uploading and connection changed, this might cause issues
        // The browser will handle it, but we log it for debugging
      }
    };

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Monitor connection type changes (Chrome/Firefox)
    const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
    if (connection) {
      connection.addEventListener('change', handleConnectionChange);
    }

    // Check initial state
    if (!navigator.onLine) {
      setState(prev => ({ ...prev, isOnline: false }));
    }

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (connection) {
        connection.removeEventListener('change', handleConnectionChange);
      }
    };
  }, [state.canRetry, state.isUploading]);

  // Cleanup on unmount
  useEffect(() => {
    // Mark component as mounted
    isComponentMountedRef.current = true;

    return () => {
      // Mark component as unmounted
      isComponentMountedRef.current = false;

      // Cancel any ongoing upload
      if (uploadAbortControllerRef.current) {
        uploadAbortControllerRef.current.abort();
      }

      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (websocketRef.current) {
        websocketRef.current.close();
      }
      if (audioElementRef.current) {
        audioElementRef.current.pause();
      }
      if (state.audioUrl) {
        URL.revokeObjectURL(state.audioUrl);
      }

      // Clear pending blob
      pendingAudioBlobRef.current = null;
    };
  }, []);

  // Format duration
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      {/* Recording Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
        {!state.audioUrl ? (
          <Button
            variant={state.isRecording ? 'contained' : 'outlined'}
            color={state.isRecording ? 'error' : 'primary'}
            size="large"
            startIcon={state.isRecording ? <Stop /> : <Mic />}
            onClick={state.isRecording ? stopRecording : startRecording}
            disabled={disabled || state.isRecording === 'starting' || state.isRecording === 'stopping'}
            sx={{
              minWidth: 150,
              height: 50,
              fontSize: '1.1rem',
            }}
          >
            {state.isRecording === 'starting' && 'Starting...'}
            {state.isRecording === 'recording' && 'Stop Recording'}
            {state.isRecording === 'stopping' && 'Stopping...'}
            {!state.isRecording && 'Start Recording'}
          </Button>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <IconButton
              onClick={togglePlayback}
              color="primary"
              size="large"
            >
              {state.isPlaying ? <Pause /> : <PlayArrow />}
            </IconButton>
            <IconButton
              onClick={deleteRecording}
              color="error"
              size="large"
            >
              <Delete />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Recording Status */}
      {state.isRecording === 'recording' && (
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Chip
            icon={<Mic />}
            label={`Recording: ${formatDuration(state.duration)}`}
            color="error"
            variant="filled"
            sx={{
              animation: 'pulse 1.5s infinite',
              '@keyframes pulse': {
                '0%': { opacity: 1 },
                '50%': { opacity: 0.7 },
                '100%': { opacity: 1 },
              },
            }}
          />
          <LinearProgress
            variant="determinate"
            value={(state.duration / maxDuration) * 100}
            color="error"
            sx={{ mt: 1, height: 4, borderRadius: 2 }}
          />
        </Box>
      )}

      {/* Audio Preview */}
      {state.audioUrl && (
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Chip
            icon={<CheckCircle />}
            label={`Recorded: ${formatDuration(state.duration)}`}
            color="success"
            variant="filled"
          />
          {state.uploadProgress > 0 && state.uploadProgress < 100 && (
            <LinearProgress
              variant="determinate"
              value={state.uploadProgress}
              sx={{ mt: 1, height: 4, borderRadius: 2 }}
            />
          )}
        </Box>
      )}

      {/* Network Status Indicator */}
      {!state.isOnline && (
        <Alert severity="warning" icon={<CloudOff />} sx={{ mt: 2 }}>
          You are offline. Recording is possible, but uploads will be retried when connection is restored.
        </Alert>
      )}

      {/* Uploading Progress */}
      {state.isUploading && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Uploading... {state.uploadProgress}%
            {state.retryCount > 0 && ` (Retry ${state.retryCount}/3)`}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={state.uploadProgress}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>
      )}

      {/* Error Display with Retry Button */}
      {state.error && !state.isUploading && (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          action={
            state.canRetry && (
              <Button
                color="inherit"
                size="small"
                startIcon={<Refresh />}
                onClick={retryUpload}
                disabled={!state.isOnline}
              >
                Retry
              </Button>
            )
          }
        >
          {state.error}
          {state.canRetry && !state.isOnline && (
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Waiting for network connection to retry...
            </Typography>
          )}
        </Alert>
      )}

      {/* Instructions */}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
        Maximum recording time: {formatDuration(maxDuration)}
      </Typography>
    </Box>
  );
};

export default AudioRecorder;
